import { useLoadingStore } from '@/modules/shared/stores/loadingStore'
import moment from 'moment'
import type { Moment } from 'moment'
import { axiosInstance } from '@/modules/shared/lib/axios'
import { format } from 'date-fns'
import {
  ActivityModel,
  CardInsertModel,
  PlaceModel,
  DayActivityModel,
  ViolationModel,
  DailyRestEventModel,
  DailyPartialRegularRestEventModel,
  WeeklyRestEventModel,
  WeeklyReducedRestEventModel,
  BreakModel,
  PartialBreakModel,
  WeekRecompenseModel,
  OutOfScopeModel,
} from '@/app/models'
import { ExistingDelegationModel } from '@/app/models/existingDelegationModel'
import { EventModel } from '@/app/models/newDelegation/eventModel'
import { mapToActivityInfo } from '@/app/mappers/activityMappers'
import { PlaceEntryType } from '@/app/constants/placeEntryType'
import { CardActivityType } from '@/app/constants/cardActivityType'

export interface DriverActivitiesData {
  activities: ReadonlyArray<ActivityModel>
  cardInserts: ReadonlyArray<CardInsertModel>
  places: ReadonlyArray<PlaceModel>
  dayActivities: ReadonlyArray<DayActivityModel>
  violations: ReadonlyArray<ViolationModel>
  dailyRests: ReadonlyArray<DailyRestEventModel>
  dailyPartialRegularRests: ReadonlyArray<DailyPartialRegularRestEventModel>
  weeklyRests: ReadonlyArray<WeeklyRestEventModel>
  weeklyReducedRests: ReadonlyArray<WeeklyReducedRestEventModel>
  breaks: ReadonlyArray<BreakModel>
  partialBreaks: ReadonlyArray<PartialBreakModel>
  weekRecompenses: ReadonlyArray<WeekRecompenseModel>
  outOfScopes: ReadonlyArray<OutOfScopeModel>
  existingDelegations: ReadonlyArray<ExistingDelegationModel>
  tripEvents: ReadonlyArray<EventModel>
}

const useActivityService = () => {
  const startLoading = useLoadingStore((s) => s.startLoading)
  const getActivity = async ({
    companyId,
    driverId,
    startTime,
    endTime,
    includeTrips,
  }: {
    companyId: string
    driverId: number
    startTime: string
    endTime: string
    includeTrips: boolean
  }) => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.get(
        `api/${companyId}/activity?&driverId=${driverId}&startTime=${startTime}&endTime=${endTime}&includeTrips=${includeTrips}`,
      )
      return res.data
    } catch (err) {
      return []
    } finally {
      stopLoading()
    }
  }

  const getDriverActivitiesForTrip = async ({
    companyId,
    driverId,
    startTime,
    endTime,
  }: {
    companyId: string
    driverId: string | number
    startTime: Moment | string | Date
    endTime: Moment | string | Date
  }): Promise<DriverActivitiesData> => {
    const stopLoading = startLoading()
    try {
      // Format dates for API
      const startTimeStr =
        startTime instanceof Date
          ? format(startTime, 'YYYY-MM-DD HH:mm')
          : moment.isMoment(startTime)
            ? startTime.format('YYYY-MM-DD HH:mm')
            : typeof startTime === 'string'
              ? startTime
              : String(startTime)

      const endTimeStr =
        endTime instanceof Date
          ? format(endTime, 'YYYY-MM-DD HH:mm')
          : moment.isMoment(endTime)
            ? endTime.format('YYYY-MM-DD HH:mm')
            : typeof endTime === 'string'
              ? endTime
              : String(endTime)

      const res = await axiosInstance.get(
        `api/${companyId}/activity?&driverId=${driverId}&startTime=${startTimeStr}&endTime=${endTimeStr}&includeTrips=true`,
      )

      // The API returns all activity data in the response
      // Use mapToActivityInfo to properly map dates to Moment objects
      const data = res.data || {}
      const mappedData = mapToActivityInfo(data)

      // mapPlace and mapCardInsert in reducer don't convert dates, so convert them here

      const mapPlaceWithDates = (place: any): PlaceModel => {
        return {
          startTime: moment.utc(place.startTime).local(),
          endTime: moment.utc(place.endTime).local(),
          entryType: place.entryType ? PlaceEntryType.BEGIN : PlaceEntryType.END,
          nation: place.nation,
        }
      }

      const mapCardInsertWithDates = (cardInsert: any): CardInsertModel => {
        return {
          startTime: moment.utc(cardInsert.startTime).local(),
          endTime: moment.utc(cardInsert.endTime).local(),
          activityType: cardInsert.activity
            ? CardActivityType.INSERTED
            : CardActivityType.NOT_INSERTED,
        }
      }

      return {
        activities: mappedData.activities || [],
        cardInserts: (data.cardInserts || []).map((c: any) => mapCardInsertWithDates(c)),
        places: (data.places || []).map((p: any) => mapPlaceWithDates(p)),
        dayActivities: mappedData.dayActivities || [],
        violations: mappedData.violations || [],
        dailyRests: mappedData.dailyRests || [],
        dailyPartialRegularRests: mappedData.dailyPartialRegularRests || [],
        weeklyRests: mappedData.weeklyRests || [],
        weeklyReducedRests: mappedData.weeklyReducedRests || [],
        breaks: mappedData.breaks || [],
        partialBreaks: mappedData.partialBreaks || [],
        weekRecompenses: mappedData.weekRecompenses || [],
        outOfScopes: mappedData.outOfScopes || [],
        existingDelegations: mappedData.existingDelegations || [],
        tripEvents: mappedData.tripEvents || [],
      }
    } catch (err) {
      console.error('Error fetching driver activities:', err)
      // Return empty arrays on error
      return {
        activities: [],
        cardInserts: [],
        places: [],
        dayActivities: [],
        violations: [],
        dailyRests: [],
        dailyPartialRegularRests: [],
        weeklyRests: [],
        weeklyReducedRests: [],
        breaks: [],
        partialBreaks: [],
        weekRecompenses: [],
        outOfScopes: [],
        existingDelegations: [],
        tripEvents: [],
      }
    } finally {
      stopLoading()
    }
  }

  return {
    getActivity,
    getDriverActivitiesForTrip,
  }
}

export default useActivityService
